import { useState, useEffect, ReactNode, useCallback, useContext, useMemo } from 'react';
import { TeamsContext, saveTeamToStorage, loadTeamFromStorage, clearTeamFromStorage, TeamsContextType } from './TeamsContext';
import { Team, TeamsApi, Membership, Invitation, RoleEnum } from 'api-client';
import { getApiConfiguration } from '../api/utils';
import { AuthContext } from './AuthContext';

export const TeamsProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useContext(AuthContext);
  
  // State management
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize API client
  const client = useMemo(() => 
    token ? new TeamsApi(getApiConfiguration(token)) : null
  , [token]);

  // Initialize teams and set default team
  const initializeTeams = useCallback(async () => {
    if (!client) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.teamsList();
      setTeams(response.results);
      
      const { teamId } = loadTeamFromStorage();
      
      if (teamId && response.results.length > 0) {
        const savedTeam = response.results.find(team => team.id.toString() === teamId);
        if (savedTeam) {
          setCurrentTeam(savedTeam);
          return;
        }
      }
      
      if (response.results.length > 0) {
        const firstTeam = response.results[0];
        setCurrentTeam(firstTeam);
        saveTeamToStorage(firstTeam);
      }
    } catch (err) {
      console.error('Error initializing teams:', err);
      setError('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  // Refetch current team data
  const refetchCurrentTeam = useCallback(async (): Promise<Team | null> => {
    if (!client || !currentTeam) return null;
    
    try {
      const response = await client.teamsRetrieve({ id: currentTeam.id });
      setCurrentTeam(response);
      saveTeamToStorage(response);
      
      // Update the team in the teams array as well
      setTeams(prev => prev.map(t => t.id === response.id ? response : t));
      
      return response;
    } catch (err) {
      console.error('Error refetching current team:', err);
      return null;
    }
  }, [client, currentTeam]);

  // Team management functions
  const switchTeam = useCallback((team: Team) => {
    setCurrentTeam(team);
    saveTeamToStorage(team);
  }, []);

  const createTeam = useCallback(async (name: string): Promise<Team> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      const response = await client.teamsCreate({
        team: {
          dashboardUrl: name,
          isAdmin: false,
          onboardingStatus: "not_started",
          slug: name,
          subscriptionType: "free",
          stealerLeaksCredits: 0
        }
      });
      setTeams(prev => [...prev, response]);
      return response;
    } catch (err) {
      console.error('Error creating team:', err);
      throw err;
    }
  }, [client]);

  const updateTeam = useCallback(async (team: Team): Promise<Team> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      const response = await client.teamsUpdate({ id: team.id, team });
      setTeams(prev => prev.map(t => t.id === team.id ? response : t));
      if (currentTeam?.id === team.id) {
        setCurrentTeam(response);
      }
      return response;
    } catch (err) {
      console.error('Error updating team:', err);
      throw err;
    }
  }, [client, currentTeam]);

  const deleteTeam = useCallback(async (teamId: number): Promise<void> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      await client.teamsDestroy({ id: teamId });
      setTeams(prev => prev.filter(t => t.id !== teamId));
      if (currentTeam?.id === teamId) {
        const remainingTeams = teams.filter(t => t.id !== teamId);
        if (remainingTeams.length > 0) {
          setCurrentTeam(remainingTeams[0]);
          saveTeamToStorage(remainingTeams[0]);
        } else {
          setCurrentTeam(null);
          clearTeamFromStorage();
        }
      }
    } catch (err) {
      console.error('Error deleting team:', err);
      throw err;
    }
  }, [client, currentTeam, teams]);

  // Team members functions
  const addMember = useCallback(async (teamSlug: string, email: string, role: string): Promise<Membership> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      const response = await client.membershipsCreate({
        teamSlug,
        membership: {
          userId: 0,
          firstName: '',
          lastName: '',
          displayName: '',
          avatarUrl: '',
          role: role as RoleEnum
        }
      });
      return response;
    } catch (err) {
      console.error('Error adding member:', err);
      throw err;
    }
  }, [client]);

  const removeMember = useCallback(async (teamSlug: string, memberId: string): Promise<void> => {
    if (!client) throw new Error('API client not initialized');
    if (!memberId) throw new Error('Member ID is required');
    if (!currentTeam) throw new Error('Current team is required');
    
    try {
      await client.membershipsDestroy({ teamSlug, id: memberId });
      await updateTeam({ ...currentTeam, members: currentTeam.members?.filter((m: Membership) => m.id !== Number(memberId)) });
    } catch (err) {
      console.error('Error removing member:', err);
      throw err;
    }
  }, [client]);

  const updateMember = useCallback(async (teamSlug: string, memberId: string, role: string): Promise<Membership> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      const response = await client.membershipsUpdate({
        teamSlug,
        id: memberId,
        membership: { 
          role: role as RoleEnum,
          userId: 0,
          firstName: '',
          lastName: '',
          displayName: '',
          avatarUrl: ''
        }
      });
      return response;
    } catch (err) {
      console.error('Error updating member:', err);
      throw err;
    }
  }, [client]);

  // Team invitations functions
  const createInvitation = useCallback(async (teamSlug: string, email: string, role: string): Promise<Invitation> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      const response = await client.invitationsCreate({
        teamSlug,
        invitation: {
          invitedBy: "0",
          teamName: teamSlug,
          email,
          role: role as RoleEnum,
        }
      });
      return response;
    } catch (err) {
      console.error('Error creating invitation:', err);
      throw err;
    }
  }, [client]);

  const cancelInvitation = useCallback(async (teamSlug: string, invitationId: string): Promise<void> => {
    if (!client) throw new Error('API client not initialized');
    
    try {
      await client.invitationsDestroy({ teamSlug, id: invitationId });
    } catch (err) {
      console.error('Error canceling invitation:', err);
      throw err;
    }
  }, [client]);

  // Initialize teams on mount
  useEffect(() => {
    if (token) {
      initializeTeams();
    }
  }, [token, initializeTeams]);

  const contextValue: TeamsContextType = {
    teams,
    currentTeam,
    isLoading,
    error,
    switchTeam,
    initializeTeams,
    createTeam,
    updateTeam,
    deleteTeam,
    addMember,
    removeMember,
    updateMember,
    createInvitation,
    cancelInvitation,
    refetchCurrentTeam
  };

  return (
    <TeamsContext.Provider value={contextValue}>
      {children}
    </TeamsContext.Provider>
  );
};